import React, { useState, useEffect } from 'react';
import API from '../utils/const';
import axios from 'axios';
import LoadingOverlay from './LoadingOverlay';
import { createToast } from './Toast';
import { MessageKind } from '../utils/common';
import { useTranslation } from 'react-i18next';
import { CalendarProvider, convertToAdminConsentUrl } from '../utils/common';
import { GoogleSVG, ICSLogoSVG, OutlookSVG } from './SVG';
import { Modal, TextInput, Button, Stack, Pill, Group, Text, Flex, Badge, Input, ActionIcon } from '@mantine/core';
import { IconClipboard } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { handleApiError } from '../utils/app';
import { useAuth } from '../contexts/AuthContext';
import { Box } from '@mantine/core';
import { useFathom } from '../contexts/FathomContext';
import { User } from '../utils/session';
import { Account } from './AccountCard';
import { MagicLinkConnect } from './MagicLinkConnect';

export const AddCalendarAccount: React.FC<{ isConnected: boolean, glowing: boolean, provider: CalendarProvider, withTos?: boolean, onSuccess?: () => void, isBeta?: boolean }> = ({ isConnected, glowing, provider, withTos = true, onSuccess = () => { }, isBeta = false }) => {
    const { t } = useTranslation(['app']);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isOutlookModalOpen, setIsOutlookModalOpen] = useState<boolean>(false);
    const [adminApprovalUrl, setAdminApprovalUrl] = useState<string>('');
    const [normalOAuthUrl, setNormalOAuthUrl] = useState<string>('');
    const { isAuthenticated } = useAuth();
    const { trackEvent } = useFathom();

    const form = useForm({
        initialValues: {
            url: '',
            name: ''
        },
        validate: {
            url: (value) => {
                if (!value) return t('dashboard.required-field');
                if (!value.startsWith('http') && !value.startsWith('webcal')) return t('common.invalid-url');
                return null;
            },
            name: (value) => {
                if (!value) return t('dashboard.required-field');
                return null;
            }
        }
    });

    const addAccount = async () => {
        trackEvent(`Add Calendar Account > ${provider} Clicked`)
        if (provider === CalendarProvider.ICS) {
            if (!isAuthenticated) {
                window.location.href = `/login?signup=true`;
                return;
            }
            setIsModalOpen(true);
            return;
        }

        if (provider === CalendarProvider.OUTLOOK) {
            setIsOutlookModalOpen(true);
            return;
        }

        try {
            setIsLoading(true);

            // get utm campaign from url
            let utmCampaign = null;
            try {
                const urlParams = new URLSearchParams(window.location.search);
                utmCampaign = urlParams.get('utm_campaign');
            } catch (error) {
                console.error('Error getting utm campaign:', error);
            }


            const response = await axios.get(
                `${API}/${provider}/calendar/prepare`,
                {
                    method: 'GET',
                    withCredentials: true,
                    params: {
                        utm_campaign: utmCampaign
                    }
                }
            );

            const data = await response.data;
            window.location.href = data.url;
        } catch (error) {
            console.error('Error fetching calendars:', error);
            createToast("An error occured while trying to add the account", MessageKind.Error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const generateAdminApprovalUrl = async () => {
            if (isOutlookModalOpen && !adminApprovalUrl) {
                try {
                    let utmCampaign = null;
                    try {
                        const urlParams = new URLSearchParams(window.location.search);
                        utmCampaign = urlParams.get('utm_campaign');
                    } catch (error) {
                        console.error('Error getting utm campaign:', error);
                    }

                    const response = await axios.get(
                        `${API}/${CalendarProvider.OUTLOOK}/calendar/prepare`,
                        {
                            method: 'GET',
                            withCredentials: true,
                            params: {
                                utm_campaign: utmCampaign
                            }
                        }
                    );

                    const data = await response.data;
                    setNormalOAuthUrl(data.url);
                    setAdminApprovalUrl(convertToAdminConsentUrl(data.url));
                } catch (error) {
                    console.error('Error generating admin approval URL:', error);
                }
            }
        };

        generateAdminApprovalUrl();
    }, [isOutlookModalOpen, adminApprovalUrl]);

    const handleOutlookNormalFlow = async () => {
        if (normalOAuthUrl) {
            window.location.href = normalOAuthUrl;
        }
    }

    const handleOutlookEnterpriseFlow = async () => {
        if (normalOAuthUrl) {
            // Change the prompt parameter to select_account instead of consent
            if (normalOAuthUrl) {
                try {
                    const url = new URL(normalOAuthUrl);
                    url.searchParams.set('prompt', 'select_account');
                    window.location.href = url.toString();
                } catch (e) {
                    // Fallback to direct redirect if URL parsing fails
                    window.location.href = normalOAuthUrl;
                }
            }
        }
    }

    const copyAdminApprovalUrl = () => {
        navigator.clipboard.writeText(adminApprovalUrl);
        createToast(t('dashboard.link_copied'), MessageKind.Success);
    };

    const handleICSSubmit = async (values: { url: string, name: string }) => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `${API}/accounts/ics`,
                {
                    method: 'POST',
                    body: JSON.stringify({ url: values.url, name: values.name }),
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.ok) {
                createToast(t('dashboard.calendar.added-message'), MessageKind.Success);
                setIsModalOpen(false);
                form.reset();
                onSuccess();
            } else {
                handleApiError(response, t, true);
            }
        } catch (error) {
            console.error('Error adding ICS calendar:', error);
            handleApiError(error, t, true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal
                opened={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                withCloseButton={false}
                centered
            >
                <form onSubmit={form.onSubmit(handleICSSubmit)}>
                    <Stack>
                        <TextInput
                            required
                            label='URL'
                            placeholder="https://example.com/... or webcal://example.com/..."
                            {...form.getInputProps('url')}
                        />
                        <TextInput
                            required
                            label={t('input.calendar-name')}
                            placeholder={t('input.calendar-name-placeholder')}
                            {...form.getInputProps('name')}
                        />
                        <Button type="submit" loading={isLoading} fullWidth>
                            {t('common.save')}
                        </Button>
                    </Stack>
                </form>
            </Modal>
            <Modal
                opened={isOutlookModalOpen}
                onClose={() => {
                    setIsOutlookModalOpen(false);
                    setAdminApprovalUrl('');
                    setNormalOAuthUrl('');
                }}
                withCloseButton={true}
                centered
                title="Connect Outlook Account"
            >
                <Stack gap="md">
                    <Text>
                        Outlook account setup may require admin approval. To check, please first try the following button:
                    </Text>
                    <Button 
                        onClick={handleOutlookNormalFlow} 
                        loading={!normalOAuthUrl}
                        fullWidth
                    >
                        Try this link
                    </Button>
                    {adminApprovalUrl && (
                        <>
                            <Text>If it gives you a "Need admin approval" error, then send this link to your admin to approve:</Text>
                            <Input 
                                value={adminApprovalUrl} 
                                onChange={() => {}} 
                                rightSection={
                                    <ActionIcon variant="light" aria-label="Copy" onClick={copyAdminApprovalUrl}>
                                        <IconClipboard style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                    </ActionIcon>
                                } 
                                rightSectionPointerEvents="all"
                                readOnly
                            />
                        </>
                    )}

                    <Text>Once it's been approved, wait a few minutes for Outlook server to update, and then follow this button:</Text>
                    <Button 
                        onClick={handleOutlookEnterpriseFlow} 
                        loading={!adminApprovalUrl}
                        fullWidth
                        variant="light"
                    >
                        Only after admin approval
                    </Button>
                </Stack>
            </Modal>
            {isLoading && <LoadingOverlay />}
            <Button c='black' bg='gray.0' variant='outline' className={`${glowing ? 'glowing' : ''}`} onClick={addAccount} px={8}>
                <Group gap='xs'>
                    <Box w={20} h={20}>
                        {provider === CalendarProvider.GOOGLE &&
                            <GoogleSVG />
                        }
                        {provider === CalendarProvider.OUTLOOK &&
                            <OutlookSVG />
                        }
                        {provider === CalendarProvider.ICS &&
                            <ICSLogoSVG />
                        }
                    </Box>
                    <Text fz={{ base: 'xs', sm: 'sm' }}>{t(`dashboard.connect-${provider}`)}</Text>
                    {isBeta &&
                        <Badge
                            variant='light'
                            c="brand.7"
                            ml='md'
                            size='xs'
                        > Beta </Badge>
                    }
                </Group>
            </Button>
            {!isConnected && withTos &&
                <div className='mx-auto mt-1 small' dangerouslySetInnerHTML={{ __html: t("common.accept-tos") }}>
                </div>
            }
        </>

    );
};

export const AddCalendarAccountWrapper: React.FC<{ user: User, accounts: Account[], updateAccounts: () => void }> = ({ user, accounts, updateAccounts }) => {
    const { isAuthenticated } = useAuth();
    return <>
        <Flex
            align="center"
            direction="column"
            maw={"350px"}
            justify="center"
            mx={"auto"}
            mb={"lg"}
        >
            <Stack>
                <AddCalendarAccount provider={CalendarProvider.GOOGLE} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={false} />
                <AddCalendarAccount provider={CalendarProvider.OUTLOOK} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={false} isBeta={false} />
                {isAuthenticated &&
                    < AddCalendarAccount provider={CalendarProvider.ICS} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={true} onSuccess={() => { updateAccounts(); }} isBeta={false} />
                }
            </Stack>
            {isAuthenticated && <MagicLinkConnect />}
        </Flex>
    </>
}

export default AddCalendarAccount;